import { useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";
import UIkit from "uikit";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import classnames from "classnames";

import { Form } from "../../../../ui";

const schema = yup.object().shape({
  location_id: yup.string().required("Location is required"),
  item_id: yup.string().required("Item is required"),
  action: yup.string().required("Action is required"),
  quantity: yup
    .number()
    .typeError("Quantity must be a number")
    .positive("Quantity must be greater than 0")
    .required("Quantity is required"),
});

const defaultValues = {
  location_id: "",
  item_id: "",
  action: "add", // add | remove | set
  quantity: "",
  note: "",
};

const ACTION_OPTIONS = [
  { label: "Add to stock", value: "add" },
  { label: "Remove from stock", value: "remove" },
  { label: "Set exact quantity", value: "set" },
];

const StorageStockAdjust = ({
  entry,              // existing stock row or null
  locations = [],     // [{ id, name }]
  items = [],         // [{ id, name, sku }]
  flagIsProcessing,
  onSave,             // async (payload) => true/false
  onCancel,           // () => void
}) => {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    reset,
    clearErrors,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues,
  });

  const locationId = watch("location_id");
  const itemId = watch("item_id");
  const action = watch("action");

  const clearForms = useCallback(() => {
    Object.keys(defaultValues).forEach((formname) => {
      setValue(formname, defaultValues[formname]);
    });
  }, [setValue]);

  // Initialize from existing entry if present
  useEffect(() => {
    clearErrors();

    const src = entry || {};
    setValue("location_id", src.location_id || defaultValues.location_id);
    setValue("item_id", src.item_id || defaultValues.item_id);
    setValue("action", defaultValues.action);
    setValue("quantity", "");
    setValue("note", "");

    if (!entry) {
      clearForms();
    }
  }, [entry, clearErrors, setValue, clearForms]);

  const handleRegister = useCallback(
    async (formdata) => {
      const payload = {
        location_id: formdata.location_id,
        item_id: formdata.item_id,
        action: formdata.action,
        quantity: formdata.quantity,
        note: formdata.note || "",
      };

      try {
        const result = await (onSave ? onSave(payload) : true);
        if (result !== false) {
          UIkit.offcanvas("#modal-storage-stock-add").hide();
          reset(defaultValues);
        }
      } catch (err) {
        UIkit.notification("Failed to adjust stock.", "error");
      }
    },
    [onSave, reset]
  );

  const handleError = useCallback((err) => {
    console.log(err);
  }, []);

  const handleClose = useCallback(() => {
    if (onCancel) onCancel();
    UIkit.offcanvas("#modal-storage-stock-add").hide();
  }, [onCancel]);

  const locationLabel =
    locations.find((l) => l.id === locationId)?.name || "Select a Location";

  const itemLabel = (() => {
    const it = items.find((i) => i.id === itemId);
    if (!it) return "Select an Item";
    return it.sku ? `${it.name} (${it.sku})` : it.name;
  })();

  const actionLabel =
    ACTION_OPTIONS.find((a) => a.value === action)?.label ||
    "Select an Action";

  return (
    <div
      id="modal-storage-stock-add"
      className="uk-modal-full hms-modals uk-modal uk-open uk-dark"
      uk-offcanvas="flip: true; overlay: true"
    >
      <div className="uk-offcanvas-bar">
        <div className="hms-modals-main">
          <button
            className="uk-offcanvas-close uk-close-large"
            type="button"
            uk-close="true"
            onClick={handleClose}
          ></button>

          <div className="uk-modal-header">
            <h2 className="uk-modal-title">
              {entry ? "Adjust Stock" : "Adjust / Add Stock"}
            </h2>
          </div>

          <div className="uk-modal-body">
            <div className="add-contact-form">
              <Form
                onSubmit={handleSubmit(handleRegister, handleError)}
                data-uk-grid="true"
              >
                {/* Location */}
                <div className="uk-width-1-1">
                  <label className="uk-form-label">Location*</label>
                  <div className="uk-form-controls">
                    <div uk-form-custom="target: > * > span:first-child">
                      <select {...register("location_id")}>
                        <option value="">Select a Location</option>
                        {locations.map((loc) => (
                          <option key={loc.id} value={loc.id}>
                            {loc.name}
                          </option>
                        ))}
                      </select>
                      <button
                        className={classnames("uk-button uk-button-default", {
                          "uk-form-danger": errors.location_id,
                        })}
                        type="button"
                        tabIndex="-1"
                      >
                        <span className="select-value">{locationLabel}</span>
                        <span
                          uk-icon="icon: chevron-down"
                          className="hms-align-right"
                        ></span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Item */}
                <div className="uk-width-1-1">
                  <label className="uk-form-label">Item*</label>
                  <div className="uk-form-controls">
                    <div uk-form-custom="target: > * > span:first-child">
                      <select {...register("item_id")}>
                        <option value="">Select an Item</option>
                        {items.map((it) => (
                          <option key={it.id} value={it.id}>
                            {it.sku ? `${it.name} (${it.sku})` : it.name}
                          </option>
                        ))}
                      </select>
                      <button
                        className={classnames("uk-button uk-button-default", {
                          "uk-form-danger": errors.item_id,
                        })}
                        type="button"
                        tabIndex="-1"
                      >
                        <span className="select-value">{itemLabel}</span>
                        <span
                          uk-icon="icon: chevron-down"
                          className="hms-align-right"
                        ></span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Action */}
                <div className="uk-width-1-1">
                  <label className="uk-form-label">Action*</label>
                  <div className="uk-form-controls">
                    <div uk-form-custom="target: > * > span:first-child">
                      <select {...register("action")}>
                        {ACTION_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      <button
                        className={classnames("uk-button uk-button-default", {
                          "uk-form-danger": errors.action,
                        })}
                        type="button"
                        tabIndex="-1"
                      >
                        <span className="select-value">{actionLabel}</span>
                        <span
                          uk-icon="icon: chevron-down"
                          className="hms-align-right"
                        ></span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Quantity */}
                <div className="uk-width-1-2@s">
                  <label className="uk-form-label">Quantity*</label>
                  <div className="uk-form-controls">
                    <input
                      className={classnames("uk-input", {
                        "uk-form-danger": errors.quantity,
                      })}
                      type="number"
                      min="1"
                      step="1"
                      {...register("quantity")}
                    />
                  </div>
                </div>

                {/* Note */}
                <div className="uk-width-1-1">
                  <label className="uk-form-label">Note (optional)</label>
                  <div className="uk-form-controls">
                    <textarea
                      className="uk-textarea"
                      rows="2"
                      {...register("note")}
                    />
                  </div>
                </div>

                {/* Buttons */}
                <div className="uk-width-1-1 uk-margin-top">
                  <button
                    className="uk-button hms-btn hms-red-btn"
                    type="submit"
                  >
                    {flagIsProcessing ? (
                      <div data-uk-spinner="ratio: 0.5;"></div>
                    ) : (
                      "Save"
                    )}
                  </button>
                  <button
                    className="uk-button hms-btn uk-offcanvas-close uk-margin-small-left"
                    onClick={handleClose}
                    type="button"
                  >
                    Cancel
                  </button>
                </div>
              </Form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StorageStockAdjust;